import type { EmojiDto, SoundDto, StickerDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { Play, Trash2 } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { FileDrop } from '../../components/FileDrop';
import { formatBytes } from '../../components/formatBytes';
import { ConfirmDialog } from '../../components/Modal';
import { EmojiField } from '../../components/EmojiField';
import { EmojiText } from '../../components/EmojiText';
import { SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { expressionsApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { appState, useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { EMOJI_NAME_RE, emojiNameFromFile, fileTypeAllowed, SOUND_ACCEPT, STICKER_ACCEPT } from './expressionFiles';

const EMOJI_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

interface Upload {
  id: number;
  name: string;
  loaded: number;
  total: number;
  error?: string;
}

let uploadSeq = 1;

/** Upload-Warteschlange mit Fortschrittsbalken. */
function useUploads() {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const run = async <T,>(name: string, total: number, fn: (onProgress: (l: number, t: number) => void) => Promise<T>): Promise<T | null> => {
    const id = uploadSeq++;
    setUploads((u) => [...u, { id, name, loaded: 0, total }]);
    try {
      const res = await fn((loaded, t) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, loaded, total: t || x.total } : x))));
      setUploads((u) => u.filter((x) => x.id !== id));
      return res;
    } catch (e) {
      setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: errorMessage(e) } : x)));
      return null;
    }
  };
  const dismiss = (id: number) => setUploads((u) => u.filter((x) => x.id !== id));
  return { uploads, run, dismiss };
}

function UploadList({ uploads, onDismiss }: { uploads: Upload[]; onDismiss: (id: number) => void }) {
  if (uploads.length === 0) return null;
  return (
    <ul className="upload-list" aria-live="polite" data-testid="upload-list">
      {uploads.map((u) => {
        const pct = u.total > 0 ? Math.round((u.loaded / u.total) * 100) : 0;
        return (
          <li key={u.id} className="upload-item" data-testid="upload-item">
            <div className="row">
              <span className="truncate" style={{ flex: 1 }}>
                {u.name}
              </span>
              {u.error ? (
                <button className="btn btn-ghost btn-sm" onClick={() => onDismiss(u.id)}>
                  Ausblenden
                </button>
              ) : (
                <span className="muted small">{pct} %</span>
              )}
            </div>
            {u.error ? (
              <span className="error-text" role="alert" data-testid="upload-error">
                {u.error}
              </span>
            ) : (
              <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={`${u.name} wird hochgeladen`}>
                <span style={{ width: `${pct}%` }} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function LimitInfo({ count, max, maxBytes, extra }: { count: number; max: number; maxBytes: number; extra?: ReactNode }) {
  return (
    <p className="limit-info" data-testid="expression-limits">
      <strong>{max > 0 ? `${count} von ${max} Plätzen belegt` : `${count} hochgeladen (unbegrenzt)`}</strong>
      <span className="muted"> · höchstens {formatBytes(maxBytes)} je Datei</span>
      {extra}
    </p>
  );
}

/** Inline-Umbenennen: Enter/Verlassen speichert, Escape verwirft. */
function InlineName({ value, onSave, label, pattern, testId }: { value: string; onSave: (v: string) => Promise<unknown>; label: string; pattern?: RegExp; testId: string }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const commit = async () => {
    if (draft === null) return;
    const v = draft.trim();
    if (v === value || !v) {
      setDraft(null);
      return;
    }
    if (pattern && !pattern.test(v)) {
      setError(true);
      return;
    }
    try {
      await onSave(v);
      setDraft(null);
      setError(false);
    } catch (e) {
      toast.error('Umbenennen fehlgeschlagen', errorMessage(e));
    }
  };
  return (
    <input
      className="input input-inline"
      value={draft ?? value}
      aria-label={label}
      aria-invalid={error}
      onChange={(e) => {
        setDraft(e.target.value);
        setError(false);
      }}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
        }
        if (e.key === 'Escape' && draft !== null) {
          e.preventDefault();
          e.stopPropagation();
          setDraft(null);
          setError(false);
        }
      }}
      data-testid={testId}
    />
  );
}

function DeleteButton({ label, onConfirm, testId }: { label: string; onConfirm: () => Promise<unknown>; testId: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button className="icon-btn" aria-label={`${label} löschen`} onClick={() => setOpen(true)} data-testid={testId}>
        <Trash2 size={16} />
      </button>
      {open && (
        <ConfirmDialog
          title={`${label} löschen?`}
          message="Das lässt sich nicht rückgängig machen."
          confirmLabel="Löschen"
          danger
          busy={busy}
          onClose={() => setOpen(false)}
          onConfirm={() => {
            setBusy(true);
            onConfirm()
              .catch((e) => toast.error('Löschen fehlgeschlagen', errorMessage(e)))
              .finally(() => {
                setBusy(false);
                setOpen(false);
              });
          }}
        />
      )}
    </>
  );
}

// ---------------- Emojis ----------------

function storeEmojis(serverId: string, fn: (list: EmojiDto[]) => EmojiDto[]): void {
  appState().update((d) => {
    d.emojis[serverId] = fn(d.emojis[serverId] ?? []);
  });
}

/** Tab „Emojis": mehrere Bilder auf einmal hochladen (Name aus Dateiname), umbenennen, löschen. */
export function EmojisTab({ serverId }: { serverId: string }) {
  const emojis = useAppStore((s) => s.emojis[serverId]) ?? [];
  const limits = useAppStore((s) => s.limits);
  const { uploads, run, dismiss } = useUploads();
  const full = limits.maxEmojisPerServer > 0 && emojis.length + uploads.length >= limits.maxEmojisPerServer;

  const add = (files: File[]) => {
    for (const file of files) {
      const err = fileTypeAllowed(file, EMOJI_ACCEPT, limits.maxEmojiBytes);
      if (err) {
        toast.error(file.name, err);
        continue;
      }
      void run(file.name, file.size, (onProgress) => expressionsApi.createEmoji(serverId, file, emojiNameFromFile(file.name), { onProgress })).then((e) => {
        if (e) storeEmojis(serverId, (l) => (l.some((x) => x.id === e.id) ? l : [...l, e]));
      });
    }
  };

  const animated = emojis.filter((e) => e.animated);
  const still = emojis.filter((e) => !e.animated);
  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Emojis">Eigene Emojis kann jedes Mitglied in Nachrichten und Reaktionen verwenden – mit dem passenden Recht auch auf anderen Servern.</SettingsHeader>
      <LimitInfo count={emojis.length} max={limits.maxEmojisPerServer} maxBytes={limits.maxEmojiBytes} extra={<span className="muted"> · Name: 2–32 Zeichen aus A–Z, 0–9, _</span>} />
      <FileDrop accept={EMOJI_ACCEPT} multiple onFiles={add} disabled={full} label="Emojis hochladen" hint="PNG, JPEG, GIF oder WebP (animiert erlaubt). Mehrere Dateien möglich." testId="emoji-upload" />
      <UploadList uploads={uploads} onDismiss={dismiss} />
      {[
        { title: 'Emojis', list: still },
        { title: 'Animierte Emojis', list: animated },
      ].map((sec) =>
        sec.list.length === 0 ? null : (
          <section key={sec.title} className="stack" style={{ gap: '0.4rem' }}>
            <h2 className="settings-subheading">
              {sec.title} <span className="muted small">({sec.list.length})</span>
            </h2>
            <ul className="settings-list" data-testid="emoji-list">
              {sec.list.map((e) => (
                <li key={e.id} className="settings-row" data-testid="emoji-row" data-emoji-id={e.id}>
                  <img src={e.url} alt={`:${e.name}:`} className="expr-thumb" loading="lazy" />
                  <InlineName
                    value={e.name}
                    label={`Name von :${e.name}:`}
                    pattern={EMOJI_NAME_RE}
                    onSave={async (name) => {
                      const res = await expressionsApi.renameEmoji(serverId, e.id, name);
                      storeEmojis(serverId, (l) => l.map((x) => (x.id === res.id ? res : x)));
                    }}
                    testId="emoji-name"
                  />
                  <DeleteButton
                    label={`:${e.name}:`}
                    onConfirm={async () => {
                      await expressionsApi.deleteEmoji(serverId, e.id);
                      storeEmojis(serverId, (l) => l.filter((x) => x.id !== e.id));
                    }}
                    testId="emoji-delete"
                  />
                </li>
              ))}
            </ul>
          </section>
        ),
      )}
      {emojis.length === 0 && uploads.length === 0 && <p className="empty">Noch keine eigenen Emojis.</p>}
    </div>
  );
}

// ---------------- Sticker ----------------

function storeStickers(serverId: string, fn: (list: StickerDto[]) => StickerDto[]): void {
  appState().update((d) => {
    d.stickers[serverId] = fn(d.stickers[serverId] ?? []);
  });
}

/** Tab „Sticker": Datei wählen, Name/Beschreibung/Tags angeben, hochladen; bearbeiten und löschen. */
export function StickersTab({ serverId }: { serverId: string }) {
  const stickers = useAppStore((s) => s.stickers[serverId]) ?? [];
  const limits = useAppStore((s) => s.limits);
  const { uploads, run, dismiss } = useUploads();
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({ name: '', description: '', tags: '' });
  const full = limits.maxStickersPerServer > 0 && stickers.length >= limits.maxStickersPerServer;

  const pick = (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const err = fileTypeAllowed(f, STICKER_ACCEPT, limits.maxStickerBytes);
    if (err) {
      toast.error(f.name, err);
      return;
    }
    setFile(f);
    setForm({ name: f.name.replace(/\.[^.]+$/, '').slice(0, 30), description: '', tags: '' });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file || form.name.trim().length < 2) return;
    const f = file;
    const fields = { name: form.name.trim(), description: form.description.trim() || undefined, tags: form.tags.trim() || undefined };
    setFile(null);
    void run(f.name, f.size, (onProgress) => expressionsApi.createSticker(serverId, f, fields, { onProgress })).then((s) => {
      if (s) storeStickers(serverId, (l) => (l.some((x) => x.id === s.id) ? l : [...l, s]));
    });
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Sticker">Große Bild-Sticker für den Chat. Erlaubt sind PNG, APNG, GIF, WebP und Lottie (JSON).</SettingsHeader>
      <LimitInfo count={stickers.length} max={limits.maxStickersPerServer} maxBytes={limits.maxStickerBytes} />
      {file ? (
        <form className="stack expr-form" onSubmit={submit} data-testid="sticker-form">
          <p className="muted small" style={{ margin: 0 }}>
            Datei: {file.name} ({formatBytes(file.size)})
          </p>
          <div className="field">
            <label htmlFor="sticker-name">Name</label>
            <input id="sticker-name" className="input" required minLength={2} maxLength={30} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sticker-name" />
          </div>
          <div className="field">
            <label htmlFor="sticker-desc">Beschreibung (optional)</label>
            <input id="sticker-desc" className="input" maxLength={100} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sticker-description" />
          </div>
          <div className="field">
            <label htmlFor="sticker-tags">Stichwörter (für die Suche, z. B. Emoji oder Wort)</label>
            <input id="sticker-tags" className="input" maxLength={200} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="sticker-tags" />
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setFile(null)}>
              Abbrechen
            </button>
            <button className="btn btn-primary" disabled={form.name.trim().length < 2} data-testid="sticker-submit">
              Hochladen
            </button>
          </div>
        </form>
      ) : (
        <FileDrop accept={STICKER_ACCEPT} onFiles={pick} disabled={full} label="Sticker hochladen" testId="sticker-upload" />
      )}
      <UploadList uploads={uploads} onDismiss={dismiss} />
      {stickers.length === 0 && uploads.length === 0 ? (
        <p className="empty">Noch keine Sticker.</p>
      ) : (
        <ul className="sticker-grid" data-testid="sticker-list">
          {stickers.map((s) => (
            <li key={s.id} className="sticker-card" data-testid="sticker-row" data-sticker-id={s.id}>
              {s.format === 'LOTTIE' ? <div className="expr-lottie">Lottie</div> : <img src={s.url} alt={s.name} loading="lazy" />}
              <InlineName
                value={s.name}
                label={`Name von Sticker ${s.name}`}
                onSave={async (name) => {
                  const res = await expressionsApi.updateSticker(serverId, s.id, { name });
                  storeStickers(serverId, (l) => l.map((x) => (x.id === res.id ? res : x)));
                }}
                testId="sticker-rename"
              />
              {s.description && <span className="muted small truncate">{s.description}</span>}
              <DeleteButton
                label={`Sticker „${s.name}"`}
                onConfirm={async () => {
                  await expressionsApi.deleteSticker(serverId, s.id);
                  storeStickers(serverId, (l) => l.filter((x) => x.id !== s.id));
                }}
                testId="sticker-delete"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------- Sounds ----------------

function storeSounds(serverId: string, fn: (list: SoundDto[]) => SoundDto[]): void {
  appState().update((d) => {
    d.sounds[serverId] = fn(d.sounds[serverId] ?? []);
  });
}

function playSound(s: Pick<SoundDto, 'url' | 'volume'>): void {
  try {
    const a = new Audio(s.url);
    a.volume = Math.max(0, Math.min(1, s.volume));
    void a.play().catch(() => undefined);
  } catch {
    /* Audio nicht verfügbar */
  }
}

/** Tab „Sounds" (Soundboard): hochladen mit Name/Emoji/Lautstärke, anhören, bearbeiten, löschen. */
export function SoundsTab({ serverId }: { serverId: string }) {
  const limits = useAppStore((s) => s.limits);
  const sounds = useAppStore((s) => s.sounds[serverId]);
  const q = useQuery({
    queryKey: qk.sounds(serverId),
    queryFn: async () => {
      const list = await expressionsApi.sounds(serverId);
      storeSounds(serverId, () => list);
      return list;
    },
  });
  const list = sounds ?? q.data ?? [];
  const { uploads, run, dismiss } = useUploads();
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({ name: '', emoji: '', volume: 1 });
  const full = limits.maxSoundsPerServer > 0 && list.length >= limits.maxSoundsPerServer;

  const pick = (files: File[]) => {
    const f = files[0];
    if (!f) return;
    const err = fileTypeAllowed(f, SOUND_ACCEPT, limits.maxSoundBytes);
    if (err) {
      toast.error(f.name, err);
      return;
    }
    setFile(f);
    setForm({ name: f.name.replace(/\.[^.]+$/, '').slice(0, 32), emoji: '', volume: 1 });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file || form.name.trim().length < 2) return;
    const f = file;
    const fields = { name: form.name.trim(), emoji: form.emoji.trim() || undefined, volume: form.volume };
    setFile(null);
    void run(f.name, f.size, (onProgress) => expressionsApi.createSound(serverId, f, fields, { onProgress })).then((s) => {
      if (s) storeSounds(serverId, (l) => (l.some((x) => x.id === s.id) ? l : [...l, s]));
    });
  };

  const patch = async (s: SoundDto, body: { name?: string; emoji?: string | null; volume?: number }) => {
    const res = await expressionsApi.updateSound(serverId, s.id, body);
    storeSounds(serverId, (l) => l.map((x) => (x.id === res.id ? res : x)));
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Sounds">Kurze Klänge für das Soundboard in Sprachkanälen (MP3, OGG oder WAV).</SettingsHeader>
      <LimitInfo
        count={list.length}
        max={limits.maxSoundsPerServer}
        maxBytes={limits.maxSoundBytes}
        extra={<span className="muted"> · höchstens {(limits.maxSoundDurationMs / 1000).toLocaleString('de-DE')} Sekunden</span>}
      />
      {file ? (
        <form className="stack expr-form" onSubmit={submit} data-testid="sound-form">
          <p className="muted small" style={{ margin: 0 }}>
            Datei: {file.name} ({formatBytes(file.size)})
          </p>
          <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className="field" style={{ flex: 3, minWidth: 180 }}>
              <label htmlFor="sound-name">Name</label>
              <input id="sound-name" className="input" required minLength={2} maxLength={32} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sound-name" />
            </div>
            <div className="field" style={{ flex: 1, minWidth: 100 }}>
              <label htmlFor="sound-emoji">Emoji</label>
              <EmojiField id="sound-emoji" value={form.emoji || null} onChange={(v) => setForm({ ...form, emoji: v ?? '' })} label="Sound-Emoji" unicodeOnly testId="sound-emoji" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="sound-volume">Lautstärke: {Math.round(form.volume * 100)} %</label>
            <input id="sound-volume" type="range" min={0} max={1} step={0.05} value={form.volume} onChange={(e) => setForm({ ...form, volume: Number(e.target.value) })} data-testid="sound-volume" />
          </div>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setFile(null)}>
              Abbrechen
            </button>
            <button className="btn btn-primary" disabled={form.name.trim().length < 2} data-testid="sound-submit">
              Hochladen
            </button>
          </div>
        </form>
      ) : (
        <FileDrop accept={SOUND_ACCEPT} onFiles={pick} disabled={full} label="Sound hochladen" testId="sound-upload" />
      )}
      <UploadList uploads={uploads} onDismiss={dismiss} />
      {q.isPending && !sounds ? (
        <div className="empty">
          <span className="spinner" aria-label="Lädt" />
        </div>
      ) : list.length === 0 && uploads.length === 0 ? (
        <p className="empty">Noch keine Sounds.</p>
      ) : (
        <ul className="settings-list" data-testid="sound-list">
          {list.map((s) => (
            <li key={s.id} className="settings-row" data-testid="sound-row" data-sound-id={s.id}>
              <button className="icon-btn" aria-label={`${s.name} abspielen`} onClick={() => playSound(s)} data-testid="sound-play">
                <Play size={16} />
              </button>
              <span className="sound-emoji" aria-hidden="true">
                {s.emoji ? <EmojiText value={s.emoji} /> : '🔊'}
              </span>
              <InlineName value={s.name} label={`Name von Sound ${s.name}`} onSave={(name) => patch(s, { name })} testId="sound-rename" />
              <span className="muted small">{(s.durationMs / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} s</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                defaultValue={s.volume}
                aria-label={`Lautstärke von ${s.name}`}
                className="sound-volume"
                onPointerUp={(e) => void patch(s, { volume: Number(e.currentTarget.value) }).catch((err) => toast.error('Fehler', errorMessage(err)))}
                onKeyUp={(e) => void patch(s, { volume: Number(e.currentTarget.value) }).catch((err) => toast.error('Fehler', errorMessage(err)))}
                data-testid="sound-row-volume"
              />
              <DeleteButton
                label={`Sound „${s.name}"`}
                onConfirm={async () => {
                  await expressionsApi.deleteSound(serverId, s.id);
                  storeSounds(serverId, (l) => l.filter((x) => x.id !== s.id));
                }}
                testId="sound-delete"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
